import { formatDate, formatRelative } from "@/lib/utils";
import { useEffect, useState } from "react";

/**
 * Date renderers. Simplified port of the Next-side `local-date.tsx`
 * — no SSR means no UTC-then-local two-step. The browser is always
 * the renderer, so we format directly in the user's local timezone
 * and skip the hydration-mismatch dance.
 *
 * `LocalRelative` keeps the 60 s tick so "2m ago" labels stay live
 * without a parent re-render.
 */

type Props = {
  value: Date | string;
  className?: string;
  withTime?: boolean;
};

function toIso(v: Date | string): string {
  return typeof v === "string" ? v : v.toISOString();
}

export function LocalDate({ value, className, withTime = false }: Props) {
  const iso = toIso(value);
  return (
    <time dateTime={iso} className={className}>
      {formatDate(iso, undefined, { withTime })}
    </time>
  );
}

export function LocalTime({ value, className }: Props) {
  const iso = toIso(value);
  const dt = new Date(iso);
  const parts = new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(dt);
  const hh = parts.find((p) => p.type === "hour")?.value ?? "00";
  const mm = parts.find((p) => p.type === "minute")?.value ?? "00";
  return (
    <time dateTime={iso} className={className}>
      {hh}:{mm}
    </time>
  );
}

export function LocalRelative({ value, className }: Props) {
  const iso = toIso(value);
  const [text, setText] = useState(() => formatRelative(iso));
  useEffect(() => {
    const t = setInterval(() => setText(formatRelative(iso)), 60_000);
    return () => clearInterval(t);
  }, [iso]);
  return (
    <time dateTime={iso} className={className}>
      {text}
    </time>
  );
}
